import { AddressType, DealerAddressType } from 'components/common/CommonTypes';
import { CommonValidation } from 'components/common/commonValidation/commonValidation';
import DealerAddress from 'components/common/dealerAddress/DealerAddress';
import DealerAddressLookup from 'components/common/dealerAddress/DealerAddressLookup';
import ErrorSummary from 'components/common/ErrorSummary/ErrorSummary';
import FormInput from 'components/common/FormInput';
import NumberInput from 'components/common/numberInput/NumberInput';
import { TyreRepairRequestContext } from 'context/TyreRepairRequestContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import AddressManager from 'components/common/address/AddressManager';
import { GoogleAnalytics } from 'classes/GoogleAnalytics';
import {
    PageNames,
    SelectOptionsYesNo,
    TyreRepairOptions
} from 'Constants/Common';
import { useTranslation } from 'react-i18next';
import RadioGroup, {
    RadioGroupOption
} from 'components/common/radioGroup/RadioGroup';
import { useAppSelector } from 'state/hooks';
import { selectIsUSSite } from 'state/selectors/site';
import { selectTyrePreCheck } from 'state/selectors/tyreRequest';

type TyreGeneralDetailsProps = {
    cancelConfirmText: string;
};

const TyreGeneralDetails = ({
    cancelConfirmText
}: TyreGeneralDetailsProps): JSX.Element => {
    const modalAddress = useRef(null);
    const pendingRepairOption = useRef('');
    const [showClearRequestAlert, setShowClearRequestAlert] = useState(false);

    const [defaultDealerAddresses, setDefaultDealerAddresses] = useState<
        AddressType[]
    >([]);
    const { t } = useTranslation();
    const isUSSite = useAppSelector(selectIsUSSite);
    const showRepairAddress = useAppSelector((s) => s.site.showRepairAddress);
    const preCheckDetails = useAppSelector(selectTyrePreCheck);
    const displayRepairAddress =
        showRepairAddress && (defaultDealerAddresses.length > 0 || !isUSSite);

    const {
        tyreRepairRequest,
        onChangeRepairOption,
        onChangeMileage,
        onDealerAddressUpdate,
        onCancelClick,
        onContinueRepairRequest,
        onCancelRepairRequest,
        onCancelUndo,
        onRepairAddressSelectionChanged,
        onGarageAddressUpdate,
        onDefaultDealerAddressUpdate,
        onClearTyreRepairRequests
    } = useContext(TyreRepairRequestContext);

    const {
        scrollToAddress,
        showCancelAlert,
        repairAddressSelectedIndex,
        repairAddresses,
        dealerAddress,
        tyreRepairRequests
    } = tyreRepairRequest;

    const [garageAddresses] = useState<AddressType[]>([]);

    const displayGarageAddress =
        !isUSSite &&
        preCheckDetails.hasBeenRepaired === SelectOptionsYesNo.Yes &&
        preCheckDetails.hasBeenPayed === SelectOptionsYesNo.No &&
        tyreRepairRequest?.repairOption === TyreRepairOptions.PreferredGarage;

    const errorList = (): Array<string | undefined> => {
        const values = [
            tyreRepairRequest.tyreFormErrors?.mileage,
            tyreRepairRequest.tyreFormErrors?.repairOption,
            displayRepairAddress || displayGarageAddress // TODO: make sure that noRepairAddressSelected error only exists in validateGeneralPage and get rid of this condition
                ? tyreRepairRequest.tyreFormErrors?.noRepairAddressSelected
                : undefined
        ];

        const errors = values.filter(Boolean);

        return errors;
    };

    const onChangeTyreRequestMileage = (
        e: React.FormEvent<HTMLInputElement>
    ): void => {
        const value = (e.target as HTMLInputElement).value;
        if (CommonValidation.isEmpty(value)) {
            onChangeMileage('');
        } else if (CommonValidation.isInteger(value)) {
            onChangeMileage(value);
        }
    };

    const onDealerAddressUpdated = (dealerAddress: DealerAddressType): void => {
        onDealerAddressUpdate(dealerAddress);
    };
    const onDefaultAddressSelectionChanged = (
        selectedAddressIndexNumber: number
    ) => {
        onRepairAddressSelectionChanged(selectedAddressIndexNumber);
        onGarageAddressUpdate(garageAddresses[0]);
    };

    const onRepairOptionChange = (repairOption: string) => {
        if (tyreRepairRequests.length) {
            setShowClearRequestAlert(true);
            pendingRepairOption.current = repairOption;
            return;
        }
        onChangeRepairOption(repairOption);
    };

    const onConfirmClearTyreRequest = () => {
        onClearTyreRepairRequests();
        setShowClearRequestAlert(false);

        if (pendingRepairOption.current) {
            onChangeRepairOption(pendingRepairOption.current);
            pendingRepairOption.current = '';
        }
    };

    const cancelButtonText =
        tyreRepairRequests.length > 1
            ? t('cancel_repair_requests')
            : t('cancel_repair_request');

    useEffect(() => {
        if (scrollToAddress && modalAddress.current) {
            modalAddress.current.focus();
        }
        GoogleAnalytics.setPageView(PageNames.AlloyGeneral);
    }, []);

    useEffect(() => {
        if (
            showRepairAddress &&
            isUSSite &&
            tyreRepairRequest.dealerAddress?.dealerStreetAddress
        ) {
            const repairAddress: AddressType = {
                addressLine1: dealerAddress.dealerStreetAddress.street || '',
                townOrCity: dealerAddress.dealerStreetAddress.city || '',
                postcode:
                    dealerAddress.dealerStreetAddress.zipOrPostalCode || '',
                country: dealerAddress.locationCountry || ''
            };

            onDefaultDealerAddressUpdate(repairAddress);
            setDefaultDealerAddresses([repairAddress]);
            onDefaultAddressSelectionChanged(repairAddressSelectedIndex + 1);
        }
    }, [
        tyreRepairRequest.dealerAddress?.dealerStreetAddress?.street,
        showRepairAddress
    ]);

    const tyreRepairOptions: RadioGroupOption[] = [
        {
            id: 'repair-option-1',
            label: t('tyre_repair_request.repair_option_1'),
            description: t(
                'tyre_repair_request.repair_option_message_dealership'
            ),
            value: TyreRepairOptions.Dealer
        },
        {
            id: 'repair-option-2',
            label: t('tyre_repair_request.repair_option_2'),
            description: t('tyre_repair_request.repair_option_message_kwik'),
            value: TyreRepairOptions.KwikFit
        },
        {
            id: 'repair-option-3',
            label: t('tyre_repair_request.repair_option_3'),
            description: t('tyre_repair_request.repair_option_message_garage'),
            value: TyreRepairOptions.PreferredGarage
        }
    ];

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='mileage-forminput'
                        label={t('alloy_repair_request.section_2_title')}
                        errorText={tyreRepairRequest.tyreFormErrors?.mileage}>
                        <div className='row repair-request-field-container'>
                            <div className='col-xs-12 col-lg-6'>
                                <NumberInput
                                    name='approx-mileage'
                                    value={
                                        tyreRepairRequest?.mileage?.toString() ||
                                        ''
                                    }
                                    onChange={onChangeTyreRequestMileage}
                                    decimalPlaces={0}
                                />
                            </div>
                        </div>
                    </FormInput>
                    {isUSSite && (
                        <FormInput
                            id='find-dealer-forminput'
                            label={t('find_dealer')}>
                            <DealerAddressLookup
                                dealerAddress={tyreRepairRequest?.dealerAddress}
                                onDealerAddressUpdated={onDealerAddressUpdated}
                            />
                            {tyreRepairRequest?.dealerAddress
                                ?.dealerStreetAddress && (
                                <DealerAddress
                                    address={tyreRepairRequest?.dealerAddress}
                                />
                            )}
                        </FormInput>
                    )}
                    {!isUSSite && (
                        <FormInput
                            id='damage-type-forminput'
                            label={t('tyre_repair_request.repair_option_label')}
                            errorText={
                                tyreRepairRequest.tyreFormErrors?.repairOption
                            }>
                            <RadioGroup
                                name='repair-option'
                                selectedValue={tyreRepairRequest?.repairOption}
                                options={tyreRepairOptions}
                                lineSeparated
                                onChange={(selectedValue: string) =>
                                    onRepairOptionChange(selectedValue)
                                }
                            />
                        </FormInput>
                    )}
                    {displayGarageAddress && (
                        <FormInput
                            id='garage-address-form-input'
                            label={t('add_garage_address_title')}
                            errorText={
                                tyreRepairRequest.tyreFormErrors
                                    ?.noRepairAddressSelected
                            }>
                            <AddressManager
                                addressSelectedIndex={
                                    repairAddressSelectedIndex
                                }
                                addresses={garageAddresses}
                                onAddressSelectionChanged={
                                    onDefaultAddressSelectionChanged
                                }
                                maxLength={1}
                                editable
                            />
                        </FormInput>
                    )}
                    {displayRepairAddress && (
                        <FormInput
                            id='repair-address-forminput'
                            label={t('alloy_repair_request.section_3_title')}
                            errorText={
                                tyreRepairRequest.tyreFormErrors
                                    ?.noRepairAddressSelected
                            }>
                            <div className='ml-3'>
                                <p>
                                    {t('alloy_repair_request.section_3_para_1')}
                                </p>
                                {!isUSSite && (
                                    <p>
                                        {t(
                                            'alloy_repair_request.section_3_para_2'
                                        )}
                                    </p>
                                )}
                            </div>
                            <div>
                                {isUSSite &&
                                    tyreRepairRequest?.dealerAddress
                                        ?.dealerStreetAddress && (
                                        <AddressManager
                                            addressSelectedIndex={
                                                repairAddressSelectedIndex
                                            }
                                            addresses={defaultDealerAddresses}
                                            onAddressSelectionChanged={
                                                onDefaultAddressSelectionChanged
                                            }
                                        />
                                    )}
                                {!isUSSite && (
                                    <AddressManager
                                        addressSelectedIndex={
                                            repairAddressSelectedIndex
                                        }
                                        addresses={repairAddresses}
                                        onAddressSelectionChanged={
                                            onRepairAddressSelectionChanged
                                        }
                                    />
                                )}
                            </div>
                            <div tabIndex={0} ref={modalAddress}></div>
                        </FormInput>
                    )}
                </div>
                <div className='card-footer page-content-footer'>
                    {tyreRepairRequest.tyreFormErrors && (
                        <ErrorSummary
                            title={t('tyre_general_errors')}
                            errors={errorList()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    id='btnContinueGeneral'
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onContinueRepairRequest}>
                    {t('button_label.continue')}
                </button>
                <button
                    id='btnCancelGeneral'
                    data-testid='danger-button'
                    className='btn btn-danger progress-button'
                    onClick={onCancelClick}>
                    {cancelButtonText}
                </button>
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
            <ConfirmAlert
                title={t('confirm_removing')}
                showAlert={showClearRequestAlert}
                onConfirm={onConfirmClearTyreRequest}
                onCancel={() => setShowClearRequestAlert(false)}
                alertText={t(
                    'tyre_repair_request.change_repair_option_confirm_text'
                )}
            />
        </>
    );
};

export default TyreGeneralDetails;
